import React, { useRef, useState, useEffect } from 'react';
import _throttle from 'lodash/throttle';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import {
  Wrapper,
  StickyNavigationContainer,
  Nav,
  Inner,
  NavItems,
  NavItem,
  NavLink,
  PhoneCTAWrapper,
  PhoneWrapper
} from './StickyNavigation.styled';
import SvgInline from '@latitude/svg-inline';
import { Link } from '@latitude/link';
import Typography from '@latitude/typography';
import Button from '@latitude/button-rebrand';

const StickyNavigation = ({
  items,
  isVisible = true,
  topOffset = 0,
  phoneNumber,
  ctaText,
  ctaUrl,
  stickyOffset = 146
}) => {
  const stickyNavRef = useRef(null);
  const [activeAnchor, setActiveAnchor] = useState(null);
  const [isSticky, setIsSticky] = useState(false);

  const handleClickNavItem = item => {
    setActiveAnchor(item.anchor);

    const anchorElem = document.getElementById(item.anchor);
    if (!anchorElem) return;

    const navHeight = stickyNavRef.current?.offsetHeight || 0;
    const elementPosition =
      anchorElem.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition =
      elementPosition - navHeight - stickyOffset - topOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    window.history.pushState(null, '', `#${item.anchor}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      const nav = stickyNavRef.current;
      if (!nav) return;

      const navTop = nav.getBoundingClientRect().top;

      if (navTop <= stickyOffset) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [stickyOffset]);

  const PhoneNumber = ({ phoneNumber }) => {
    return (
      <Link
        noStyle
        trackId="sticky-navigation-phone"
        href={`tel:${phoneNumber}`}
        trackEventData={{
          label: `${phoneNumber}`
        }}
      >
        <PhoneWrapper>
          <SvgInline strokeBlueBright className="phone-icon" name="phone" />
          <Typography.Body small color={REBRAND_COLOR.BLACK_70}>
            {phoneNumber}
          </Typography.Body>
        </PhoneWrapper>
      </Link>
    );
  };

  return (
    <StickyNavigationContainer
      isVisible={isVisible}
      stickyOffset={stickyOffset}
    >
      <Wrapper>
        <Nav ref={stickyNavRef}>
          <Inner>
            <NavItems>
              {items?.map(item => (
                <NavItem
                  key={item.anchor}
                  $active={activeAnchor === item.anchor}
                >
                  <NavLink
                    href={`#${item.anchor}`}
                    onClick={e => {
                      e.preventDefault();
                      handleClickNavItem(item);
                    }}
                  >
                    <Typography.LinkSmall color={REBRAND_COLOR.BLACK_70}>
                      {' '}
                      {item.label}
                    </Typography.LinkSmall>
                  </NavLink>
                </NavItem>
              ))}
            </NavItems>
            {isSticky && (
              <PhoneCTAWrapper>
                {phoneNumber && <PhoneNumber phoneNumber={phoneNumber} />}

                {ctaUrl && (
                  <Button
                    className={`sticky-nav-cta`}
                    variant="primary"
                    size="md"
                    ctaText={ctaText}
                    ctaUrl={ctaUrl}
                    trackEventData={{
                      category: 'cta',
                      action: 'application-link',
                      label: ctaText,
                      location: 'Sticky Navigation'
                    }}
                    trackId="sticky-nav-apply-now"
                  />
                )}
              </PhoneCTAWrapper>
            )}
          </Inner>
        </Nav>
      </Wrapper>
    </StickyNavigationContainer>
  );
};

export default StickyNavigation;
